<template>
    <div class="dashboard tk-create-page">
        <PageBanner
            title="Create Client Engagement"
            subtext="Create an engagement with or without a referral document."
        />

        <main class="tk-create-content">
            <section class="tk-panel">
                <DocumentFormDefinitionsEditor v-if="isDeveloper" />

                <q-banner
                    v-if="validationErrors.length"
                    class="bg-red-1 text-negative q-mb-md"
                    rounded
                >
                    <div class="text-weight-bold">Resolve these items before approval:</div>
                    <button
                        v-for="error in validationErrors"
                        :key="`${error.step}-${error.message}`"
                        type="button"
                        class="validation-link"
                        @click="step = error.step"
                    >
                        {{ error.message }}
                    </button>
                </q-banner>

                <q-stepper
                    v-model="step"
                    class="workflow-stepper"
                    color="primary"
                    animated
                    flat
                    bordered
                    keep-alive
                >
                    <q-step
                        :name="1"
                        title="Person"
                        caption="Select or propose"
                        icon="person_search"
                        :done="step > 1"
                        :error="hasStepError(1)"
                    >
                        <p class="step-intro">
                            Choose the person this engagement is for. You can select an existing
                            person or enter a new person's details before the final review.
                        </p>
                        <q-option-group
                            v-model="personMode"
                            :options="personModeOptions"
                            type="radio"
                            color="primary"
                            inline
                            @update:model-value="markDirty"
                        />
                        <div v-if="personMode === 'existing'" class="q-mt-lg">
                            <TkPersonSelect
                                :model-value="selectedPerson"
                                origin-service="service_engagements"
                                :allow-anonymous="false"
                                @update:model-value="setSelectedPerson"
                            >
                                <template #actions />
                            </TkPersonSelect>
                        </div>
                        <q-banner v-else class="bg-blue-1 text-blue-10 q-mt-lg" rounded>
                            The new person will not be created until you approve the final review.
                        </q-banner>
                        <q-stepper-navigation>
                            <q-btn
                                unelevated
                                no-caps
                                color="primary"
                                label="Continue to referral"
                                :disable="personMode === 'existing' && !selectedPerson"
                                @click="step = 2"
                            />
                        </q-stepper-navigation>
                    </q-step>

                    <q-step
                        :name="2"
                        title="Referral"
                        caption="Extract or skip"
                        icon="upload_file"
                        :done="step > 2"
                        :error="hasStepError(2)"
                    >
                        <p class="step-intro">
                            Choose whether to extract information from a referral document or continue
                            without one.
                        </p>
                        <q-option-group
                            :model-value="referralMode"
                            :options="referralModeOptions"
                            class="workflow-choice"
                            type="radio"
                            color="primary"
                            @update:model-value="setReferralMode"
                        />
                        <DocumentScrapingPanel
                            v-if="referralMode === 'extract' && !acceptedReferral"
                            class="q-mt-lg"
                            @accepted="acceptReferral"
                            @changed="markDirty"
                            @declined="clearReferral"
                            @status="setReferralPanelStatus"
                        />
                        <q-banner
                            v-if="referralMode === 'extract' && acceptedReferral"
                            class="bg-green-1 text-green-9 q-mt-md"
                            rounded
                        >
                            <div class="row items-center justify-between q-gutter-sm">
                                <span>
                                    <strong>Referral ready:</strong>
                                    {{ referralFile.name }}
                                </span>
                                <q-btn
                                    flat
                                    dense
                                    no-caps
                                    color="negative"
                                    label="Remove referral"
                                    @click="clearReferral"
                                />
                            </div>
                        </q-banner>
                        <q-banner
                            v-if="referralMode === 'none'"
                            class="bg-blue-1 text-blue-10 q-mt-lg"
                            rounded
                        >
                            No referral document will be attached. You can enter the remaining
                            details yourself.
                        </q-banner>
                        <p v-if="referralPanelBlocked" class="text-orange-10 q-mt-md q-mb-none">
                            Finish reviewing the current extraction, or clear its file, before continuing.
                        </p>
                        <q-stepper-navigation class="q-gutter-sm">
                            <q-btn flat no-caps color="primary" label="Back" @click="step = 1" />
                            <q-btn
                                unelevated
                                no-caps
                                color="primary"
                                label="Continue to person details"
                                :disable="!canContinueFromReferral"
                                @click="step = 3"
                            />
                        </q-stepper-navigation>
                    </q-step>

                    <q-step
                        :name="3"
                        title="Person details"
                        caption="Review or compare"
                        icon="badge"
                        :done="step > 3"
                        :error="hasStepError(3)"
                    >
                        <p class="step-intro">
                            {{ personMode === "existing"
                                ? "Compare extracted demographics with the selected person. Stored details will not be changed."
                                : "Review and edit the new person's details before continuing." }}
                        </p>
                        <q-form ref="personForm">
                            <ReferralPersonSection
                                :mode="personMode"
                                :selected-person="selectedPerson"
                                :person-draft="personDraft"
                                :extraction-fields="extractionFields"
                                @update:person-draft="setPersonDraft"
                            />
                        </q-form>
                        <q-stepper-navigation class="q-gutter-sm">
                            <q-btn flat no-caps color="primary" label="Back" @click="step = 2" />
                            <q-btn
                                unelevated
                                no-caps
                                color="primary"
                                label="Continue to Client Engagement"
                                @click="continueFromPerson"
                            />
                        </q-stepper-navigation>
                    </q-step>

                    <q-step
                        :name="4"
                        title="Client Engagement"
                        caption="Review editable fields"
                        icon="work_outline"
                        :done="step > 4"
                        :error="hasStepError(4)"
                    >
                        <p class="step-intro">
                            Review all Client Engagement details. Extracted values remain editable and
                            existing validation rules apply.
                        </p>
                        <q-form ref="engagementForm">
                            <EngagementDraftFields
                                :draft="engagementDraft"
                                :service-options="serviceOptions"
                                :staff-options="staffOptions"
                                :referral-source-options="referralSourceOptions"
                                :risk-options="riskOptions"
                                :checklist-options="checklistOptions"
                                :extraction-fields="extractionFields"
                                :additional-worker-options="additionalWorkerOptions"
                                @update:draft="setEngagementDraft"
                            />
                        </q-form>
                        <q-stepper-navigation class="q-gutter-sm">
                            <q-btn flat no-caps color="primary" label="Back" @click="step = 3" />
                            <q-btn
                                unelevated
                                no-caps
                                color="primary"
                                :label="hasReferralSpecificInformation
                                    ? 'Continue to referral-specific information'
                                    : 'Continue to review'"
                                @click="continueFromEngagement"
                            />
                        </q-stepper-navigation>
                    </q-step>

                    <q-step
                        v-if="hasReferralSpecificInformation"
                        :name="5"
                        title="Referral-specific"
                        caption="Check extra details"
                        icon="list_alt"
                        :done="step > 5"
                    >
                        <p class="step-intro">
                            These details are specific to {{ acceptedReferral.formName }}. Check them
                            before they are saved with the Client Engagement.
                        </p>
                        <ReferralSpecificInformation
                            :fields="referralSpecificInformation"
                            @update:field="updateReferralSpecificField"
                        />
                        <q-stepper-navigation class="q-gutter-sm">
                            <q-btn flat no-caps color="primary" label="Back" @click="step = 4" />
                            <q-btn
                                unelevated
                                no-caps
                                color="primary"
                                label="Continue to review"
                                @click="goToReview"
                            />
                        </q-stepper-navigation>
                    </q-step>

                    <q-step
                        :name="6"
                        title="Review and create"
                        caption="Confirm details"
                        icon="fact_check"
                        :error="hasStepError(6)"
                    >
                        <ReferralReviewSummary
                            :person-mode="personMode"
                            :selected-person="selectedPerson"
                            :person-draft="personDraft"
                            :engagement="engagementDraft"
                            :service-options="serviceOptions"
                            :referral-source-options="referralSourceOptions"
                            :referral-file="referralFile"
                            :form-name="acceptedReferral ? acceptedReferral.formName : null"
                            :referral-specific-count="referralSpecificInformation.length"
                        />
                        <q-stepper-navigation class="review-actions">
                            <q-btn
                                flat
                                no-caps
                                color="primary"
                                label="Back"
                                @click="step = hasReferralSpecificInformation ? 5 : 4"
                            />
                            <div class="col" />
                            <q-btn
                                flat
                                no-caps
                                color="negative"
                                label="Cancel"
                                :disable="saving"
                                @click="cancelWorkflow"
                            />
                            <q-btn
                                unelevated
                                no-caps
                                color="primary"
                                icon="check"
                                label="Approve and create"
                                :loading="saving"
                                :disable="saving"
                                @click="approveAndCreate"
                            />
                        </q-stepper-navigation>
                    </q-step>
                </q-stepper>
            </section>
        </main>
    </div>
</template>

<script>
import DocumentFormDefinitionsEditor from "@/components/TkClients/DocumentFormDefinitionsEditor.vue";
import DocumentScrapingPanel from "@/components/TkClients/DocumentScrapingPanel.vue";
import EngagementDraftFields from "@/components/TkClients/EngagementDraftFields.vue";
import PageBanner from "@/components/ClientComponents/DashboardComponents/PageBanner.vue";
import ReferralPersonSection from "@/components/TkClients/ReferralPersonSection.vue";
import ReferralReviewSummary from "@/components/TkClients/ReferralReviewSummary.vue";
import ReferralSpecificInformation from "@/components/TkClients/ReferralSpecificInformation.vue";
import TkPersonSelect from "@/components/ClientComponents/TemporaryClients/TkPersonSelect.vue";
import api from "@/services/api/api";
import {
    applyEngagementExtraction,
    applyPersonExtraction,
    collectReferralWorkflowErrors,
    createReferralApprovalPayload,
    createEmptyPersonDraft,
    referralSpecificInformationFields,
    updateReviewedField,
} from "@/services/util/referralWorkflow";
import notify from "@/services/util/notify";
import token from "@/services/util/roleHandler";
import {
    needsTierOptions,
    staffOption,
    toDDMMYYYY,
} from "@/services/util/tkClients";

const createEngagementDraft = (serviceCode) => ({
    serviceCode,
    assignedWorker: null,
    additionalWorkers: [],
    referralSource: null,
    riskFactor: null,
    enteredDate: toDDMMYYYY(new Date()),
    checklistDefinitionIds: [],
    customChecklistLabels: [],
    customChecklistInput: "",
});

const mapLabelToOptionValue = (value, options) => {
    if (value === null || value === undefined || value === "") return value;
    const normalizedValue = String(value).trim().toLowerCase();
    const byValue = options.find(option => String(option.value) === String(value));
    if (byValue) return byValue.value;
    const byLabel = options.find(option => String(option.label || "").trim().toLowerCase() === normalizedValue);
    return byLabel ? byLabel.value : value;
};

export default {
    name: "TkCreateEngagement",
    components: {
        DocumentFormDefinitionsEditor,
        DocumentScrapingPanel,
        EngagementDraftFields,
        PageBanner,
        ReferralPersonSection,
        ReferralReviewSummary,
        ReferralSpecificInformation,
        TkPersonSelect,
    },
    data() {
        return {
            step: 1,
            saving: false,
            dirty: false,
            completed: false,
            referralMode: null,
            referralModeOptions: [
                {
                    label: "Upload and extract a referral document",
                    value: "extract",
                },
                {
                    label: "Continue without a referral document",
                    value: "none",
                },
            ],
            personMode: "existing",
            personModeOptions: [
                { label: "Select an existing person", value: "existing" },
                { label: "Enter a new person", value: "new" },
            ],
            selectedPerson: null,
            personDraft: createEmptyPersonDraft(),
            personDraftBeforeReferral: null,
            acceptedReferral: null,
            referralFile: null,
            referralPanelStatus: {
                hasFile: false,
                hasResult: false,
                loading: false,
            },
            extractionFields: [],
            validationErrors: [],
            lookups: {
                services: [],
                referral_sources: [],
                checklist_item_definitions: [],
            },
            staff: [],
            engagementDraft: createEngagementDraft(this.$route.query.service || null),
            engagementDraftBeforeReferral: null,
        };
    },
    computed: {
        isDeveloper() {
            return token.getRole() === "developer";
        },
        serviceOptions() {
            return this.lookups.services.map(service => ({
                label: service.name,
                value: service.code,
            }));
        },
        referralSourceOptions() {
            return (this.lookups.referral_sources || []).map(source => ({
                label: source.name,
                value: source.code,
            }));
        },
        staffOptions() {
            return this.staff.map(staffOption);
        },
        additionalWorkerOptions() {
            return this.staffOptions.filter(option => option.value !== this.engagementDraft.assignedWorker);
        },
        riskOptions() {
            return needsTierOptions;
        },
        checklistOptions() {
            return (this.lookups.checklist_item_definitions || []).map(item => ({
                label: item.label,
                value: item.id,
            }));
        },
        referralSpecificInformation() {
            return referralSpecificInformationFields(this.extractionFields);
        },
        hasReferralSpecificInformation() {
            return this.referralSpecificInformation.length > 0;
        },
        referralPanelBlocked() {
            return (
                this.referralPanelStatus.hasFile
                || this.referralPanelStatus.hasResult
                || this.referralPanelStatus.loading
            );
        },
        canContinueFromReferral() {
            if (this.referralMode === "none") return true;
            return (
                this.referralMode === "extract"
                && Boolean(this.acceptedReferral)
                && !this.referralPanelBlocked
            );
        },
    },
    async mounted() {
        window.addEventListener("beforeunload", this.warnBeforeUnload);
        await Promise.all([this.loadLookups(), this.loadStaff()]);
        this.setCurrentStaffDefault();
    },
    beforeUnmount() {
        window.removeEventListener("beforeunload", this.warnBeforeUnload);
    },
    beforeRouteLeave(to, from, next) {
        if (this.completed || !this.dirty || window.confirm("Discard this unapproved Client Engagement draft?")) {
            next();
            return;
        }
        next(false);
    },
    methods: {
        markDirty() {
            this.dirty = true;
            this.validationErrors = [];
        },
        hasStepError(step) {
            return this.validationErrors.some(error => error.step === step);
        },
        setSelectedPerson(person) {
            this.selectedPerson = person;
            this.markDirty();
        },
        setPersonDraft(personDraft) {
            this.personDraft = { ...personDraft };
            this.markDirty();
        },
        setEngagementDraft(engagementDraft) {
            this.engagementDraft = {
                ...engagementDraft,
                checklistDefinitionIds: [...engagementDraft.checklistDefinitionIds],
                customChecklistLabels: [...engagementDraft.customChecklistLabels],
            };
            this.markDirty();
        },
        setReferralMode(referralMode) {
            this.referralMode = referralMode;
            if (referralMode === "none") {
                this.clearReferral();
                return;
            }
            this.markDirty();
        },
        acceptReferral(referral) {
            this.referralMode = "extract";
            this.personDraftBeforeReferral = { ...this.personDraft };
            this.engagementDraftBeforeReferral = {
                ...this.engagementDraft,
                checklistDefinitionIds: [...this.engagementDraft.checklistDefinitionIds],
                customChecklistLabels: [...this.engagementDraft.customChecklistLabels],
            };
            this.acceptedReferral = {
                definitionId: referral.definitionId,
                definitionUpdatedAt: referral.definitionUpdatedAt,
                formType: referral.formType,
                formName: referral.formName,
            };
            this.referralFile = referral.file;
            this.extractionFields = referral.fields.map(field => ({
                ...field,
                destination: field.destination ? { ...field.destination } : null,
                metadata: field.metadata ? { ...field.metadata } : null,
            }));
            this.personDraft = applyPersonExtraction(this.personDraft, this.extractionFields);
            this.engagementDraft = applyEngagementExtraction(
                this.engagementDraft,
                this.extractionFields
            );
            this.engagementDraft = {
                ...this.engagementDraft,
                serviceCode: mapLabelToOptionValue(this.engagementDraft.serviceCode, this.serviceOptions),
                referralSource: mapLabelToOptionValue(this.engagementDraft.referralSource, this.referralSourceOptions),
                riskFactor: mapLabelToOptionValue(this.engagementDraft.riskFactor, this.riskOptions),
            };
            this.markDirty();
        },
        clearReferral() {
            if (this.acceptedReferral && this.personDraftBeforeReferral) {
                this.personDraft = { ...this.personDraftBeforeReferral };
            }
            if (this.acceptedReferral && this.engagementDraftBeforeReferral) {
                this.engagementDraft = {
                    ...this.engagementDraftBeforeReferral,
                    checklistDefinitionIds: [
                        ...this.engagementDraftBeforeReferral.checklistDefinitionIds,
                    ],
                    customChecklistLabels: [
                        ...this.engagementDraftBeforeReferral.customChecklistLabels,
                    ],
                };
            }
            this.acceptedReferral = null;
            this.referralFile = null;
            this.extractionFields = [];
            this.personDraftBeforeReferral = null;
            this.engagementDraftBeforeReferral = null;
            this.referralPanelStatus = {
                hasFile: false,
                hasResult: false,
                loading: false,
            };
            this.markDirty();
        },
        setReferralPanelStatus(status) {
            this.referralPanelStatus = {
                hasFile: Boolean(status.hasFile),
                hasResult: Boolean(status.hasResult),
                loading: Boolean(status.loading),
            };
        },
        updateReferralSpecificField({ fieldKey, value }) {
            this.extractionFields = updateReviewedField(
                this.extractionFields,
                fieldKey,
                value
            );
            this.markDirty();
        },
        async continueFromPerson() {
            if (this.personMode === "new") {
                const valid = await this.$refs.personForm.validate();
                if (!valid) return;
            }
            this.step = 4;
        },
        async continueFromEngagement() {
            const valid = await this.$refs.engagementForm.validate();
            if (!valid) return;
            if (this.hasReferralSpecificInformation) {
                this.step = 5;
                return;
            }
            this.goToReview();
        },
        goToReview() {
            const errors = this.collectValidationErrors();
            this.validationErrors = errors;
            if (errors.length) {
                this.step = errors[0].step;
                return;
            }
            this.step = 6;
        },
        collectValidationErrors() {
            return collectReferralWorkflowErrors({
                referralMode: this.referralMode,
                acceptedReferral: this.acceptedReferral,
                personMode: this.personMode,
                selectedPerson: this.selectedPerson,
                personDraft: this.personDraft,
                engagementDraft: this.engagementDraft,
            });
        },
        async approveAndCreate() {
            const errors = this.collectValidationErrors();
            this.validationErrors = errors;
            if (errors.length) {
                this.step = errors[0].step;
                return;
            }

            this.saving = true;
            let response;
            try {
                response = await api.referralEngagements.approve(
                    createReferralApprovalPayload({
                        personMode: this.personMode,
                        selectedPerson: this.selectedPerson,
                        personDraft: this.personDraft,
                        engagementDraft: this.engagementDraft,
                        acceptedReferral: this.acceptedReferral,
                        extractionFields: this.extractionFields,
                    }),
                    this.referralFile
                );
            } catch (error) {
                const responseMessage = typeof error?.response?.data === "string"
                    ? error.response.data
                    : error?.response?.statusText;
                notify.error(
                    responseMessage && responseMessage !== "Internal Server Error"
                        ? responseMessage
                        : "Referral approval failed. No records were created.",
                    "top"
                );
                return;
            } finally {
                this.saving = false;
            }

            this.completed = true;
            this.dirty = false;
            notify.primary("Person and Client Engagement approval completed", "top");
            try {
                await this.$router.push({
                    name: "tkEngagementDetail",
                    params: {
                        personId: response.data.personId,
                        engagementId: response.data.engagementId,
                    },
                });
            } catch {
                notify.error(
                    "The records were created, but the Client Engagement page could not be opened.",
                    "top"
                );
            }
        },
        cancelWorkflow() {
            if (this.dirty && !window.confirm("Discard this unapproved Client Engagement draft?")) {
                return;
            }
            this.completed = true;
            this.$router.push({ name: "tkClients" });
        },
        warnBeforeUnload(event) {
            if (!this.dirty || this.completed) return;
            event.preventDefault();
            event.returnValue = "";
        },
        async loadLookups() {
            try {
                const response = await api.tkPeople.getEngagementLookups();
                this.lookups = response.data;
                const serviceCode = this.engagementDraft.serviceCode
                    || this.lookups.services[0]?.code
                    || null;
                const checklistDefinitionIds = (
                    this.lookups.checklist_item_definitions || []
                )
                    .filter(item => item.is_default)
                    .map(item => item.id);
                this.engagementDraft = {
                    ...this.engagementDraft,
                    serviceCode,
                    checklistDefinitionIds,
                };
            } catch (error) {
                const message = error?.response?.statusText;
                notify.error(
                    message && message !== "Internal Server Error"
                        ? message
                        : "Failed to load Client Engagement options",
                    "top"
                );
            }
        },
        async loadStaff() {
            try {
                const response = await api.users.getAppStaffList();
                this.staff = response.data || [];
            } catch (error) {
                const message = error?.response?.statusText;
                notify.error(
                    message && message !== "Internal Server Error"
                        ? message
                        : "Failed to load staff options",
                    "top"
                );
            }
        },
        setCurrentStaffDefault() {
            const email = token.getEmail();
            const currentStaff = this.staff.find(staff => (
                String(staff.email || "").toLowerCase() === String(email || "").toLowerCase()
            ));
            this.engagementDraft = {
                ...this.engagementDraft,
                assignedWorker: currentStaff?.id || null,
            };
        },
    },
};
</script>

<style scoped>
.tk-create-page,
.tk-create-content {
    width: 100%;
}

.tk-create-content {
    background: white;
}

.tk-panel {
    padding: 24px;
    background: white;
    border-bottom: 1px solid rgba(47, 62, 70, 0.08);
}

.workflow-stepper {
    max-width: 1120px;
    margin: 0 auto;
}

.workflow-stepper :deep(.q-stepper__header) {
    flex-wrap: nowrap;
    overflow-x: auto;
}

.workflow-stepper :deep(.q-stepper__tab) {
    flex: 1 1 0;
    min-width: 0;
    padding: 16px 10px;
}

.workflow-stepper :deep(.q-stepper__label) {
    min-width: 0;
}

.workflow-stepper :deep(.q-stepper__title),
.workflow-stepper :deep(.q-stepper__caption) {
    white-space: normal;
}

.workflow-choice {
    display: grid;
    gap: 8px;
    max-width: 520px;
}

.step-intro {
    max-width: 820px;
    margin: 0 0 18px;
    color: #5d6b70;
    line-height: 1.5;
}

.validation-link {
    display: block;
    padding: 3px 0;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font: inherit;
    text-align: left;
    text-decoration: underline;
}

.review-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

@media (max-width: 700px) {
    .tk-panel {
        padding: 16px 10px;
    }

    .review-actions {
        align-items: stretch;
        flex-direction: column;
    }

    .review-actions .col {
        display: none;
    }

    .workflow-stepper :deep(.q-stepper__tab) {
        flex: 0 0 136px;
        padding: 12px 8px;
    }

    .workflow-stepper :deep(.q-stepper__caption) {
        display: none;
    }
}
</style>
